import React, { useState } from "react";
import { toast } from "sonner";
import { Lock, Check, Sparkles } from "lucide-react";
import { useLocation } from "react-router-dom";
import AppLayout from "./AppLayout";
import { useAsaAuth } from "../AuthContext";
import { TIERS, tierById, tierPrice, gbp } from "../data";
import { startSubscriptionCheckout } from "../payments";

// Shown when a user's tier is below the level required for a Phase 2/3 page.
export default function UpgradeScreen({ requiredTierId, title }) {
  const { user } = useAsaAuth();
  const location = useLocation();
  const tier = tierById(requiredTierId);
  const [billing, setBilling] = useState(user?.billing || "monthly");
  const [loading, setLoading] = useState(false);
  const price = tierPrice(requiredTierId, billing);

  const startUpgrade = async () => {
    setLoading(true);
    try {
      await startSubscriptionCheckout({ tier: requiredTierId, billing, kind: "upgrade", email: user?.email, extra: { returnTo: location.pathname } });
    } catch (e) {
      setLoading(false);
      toast.error("Could not start checkout. Please try again.");
    }
  };

  return (
    <AppLayout title={title || "Upgrade"}>
      <div className="max-w-2xl mx-auto">
        <div className="bg-gradient-to-r from-[#0d9488] to-[#0f766e] rounded-2xl p-8 text-white text-center">
          <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center mx-auto"><Lock size={28} /></div>
          <h2 className="font-head font-bold text-2xl mt-4">Unlock {tier.name}</h2>
          <p className="text-white/85 mt-1">{tier.tagline} Upgrade to ASAL Pro to unlock this feature.</p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6 mt-5">
          <div className="flex items-center justify-center gap-1 bg-slate-100 p-1 rounded-lg w-fit mx-auto mb-5">
            {["monthly", "yearly"].map((b) => (
              <button key={b} onClick={() => setBilling(b)} className={`px-4 py-2 rounded-md text-sm font-semibold capitalize transition-colors ${billing === b ? "bg-white text-[#0f766e] shadow-sm" : "text-slate-500"}`}>{b}{b === "yearly" && " (2 mo free)"}</button>
            ))}
          </div>
          <div className="text-center">
            <span className="text-4xl font-head font-bold text-slate-800">{gbp(price)}</span>
            <span className="text-slate-400">/{billing === "yearly" ? "year" : "month"}</span>
          </div>
          <ul className="mt-5 space-y-2 max-w-sm mx-auto">
            {tier.features.map((f) => (
              <li key={f} className="flex items-start gap-2 text-[14px] text-slate-600"><Check size={17} className="text-[#0d9488] mt-0.5 shrink-0" /> {f}</li>
            ))}
          </ul>
          <button onClick={startUpgrade} disabled={loading} className="w-full mt-6 h-12 rounded-lg bg-[#0d9488] hover:bg-[#0f766e] disabled:opacity-60 text-white font-semibold inline-flex items-center justify-center gap-2 transition-colors">
            <Sparkles size={18} /> {loading ? "Redirecting to secure checkout…" : `Upgrade to ${tier.name} — ${gbp(price)}`}
          </button>
          <p className="text-[12px] text-slate-400 text-center mt-3">Your current plan: {tierById(user?.tier).name}. Cancel or change anytime.</p>
        </div>
      </div>
    </AppLayout>
  );
}
